import { useMutation } from '@apollo/client';
import {
  assignBed,
  assignPriority,
  setPatientReadyInPACU,
  setPatientReadyInPOSTOP,
  setPatientReadyInPREP,
  setReadyForAnesthesia,
  setSpinalTimeout,
  setPatientReadyForSurgeon,
  setFamilyReadyForPickupPACU,
  setFamilyReadyForPickupPOSTOP,
  setHoldProcedure,
  setUndoHoldProcedure,
  setBlockNerve,
  setUndoBlockNerve,
  setPatientNotReadyInPREP,
  setPatientNotReadyInPACU,
  setPatientNotReadyInPOSTOP,
  setNotReadyForAnesthesia,
  setUndoSpinalTimeout,
  setPatientNotReadyForSurgeon,
  discharge,
  dischargePhysicianLastPatient,
  dischargeAnesthesiologistLastPatient,
  dischargeBothLastPatient,
  transferToChair,
  transferToPT,
  ptComplete,
  setReadyToSeeFamilyPreOp,
  setNotReadyToSeeFamilyPreOp,
  editPatientOobWarningDelayReason,
  editPatientPtWarningDelayReason,
  editPatientPtCompleteWarningDelayReason,
  editPatientPacuOutWarningDelayReason,
} from '@/graph/patients';
import { setPatientEventTimestamp } from '@/graph/procedureSteps';
import { useSetRoomMutation } from '@/hooks/useSetRoomMutation';

/**
 * Custom hook that consolidates all 30+ patient mutations into a single hook.
 * This replaces the 30+ withXXXMutation HOCs from the old implementation.
 *
 * @returns Object containing all mutation functions
 */
export function usePatientMutations() {
  const [assignBedMutation] = useMutation(assignBed);
  const [assignPriorityMutation] = useMutation(assignPriority);
  const [setPatientReadyInPACUMutation] = useMutation(setPatientReadyInPACU);
  const [setPatientReadyInPOSTOPMutation] = useMutation(setPatientReadyInPOSTOP);
  const [setPatientReadyInPREPMutation] = useMutation(setPatientReadyInPREP);
  const [setReadyForAnesthesiaMutation] = useMutation(setReadyForAnesthesia);
  const [setSpinalTimeoutMutation] = useMutation(setSpinalTimeout);
  const [setPatientReadyForSurgeonMutation] = useMutation(setPatientReadyForSurgeon);
  const [setFamilyReadyForPickupPACUMutation] = useMutation(setFamilyReadyForPickupPACU);
  const [setFamilyReadyForPickupPOSTOPMutation] = useMutation(setFamilyReadyForPickupPOSTOP);
  const [setHoldProcedureMutation] = useMutation(setHoldProcedure);
  const [setUndoHoldProcedureMutation] = useMutation(setUndoHoldProcedure);
  const [setBlockNerveMutation] = useMutation(setBlockNerve);
  const [setUndoBlockNerveMutation] = useMutation(setUndoBlockNerve);
  const [setPatientNotReadyInPREPMutation] = useMutation(setPatientNotReadyInPREP);
  const [setPatientNotReadyInPACUMutation] = useMutation(setPatientNotReadyInPACU);
  const [setPatientNotReadyInPOSTOPMutation] = useMutation(setPatientNotReadyInPOSTOP);
  const [setNotReadyForAnesthesiaMutation] = useMutation(setNotReadyForAnesthesia);
  const [setUndoSpinalTimeoutMutation] = useMutation(setUndoSpinalTimeout);
  const [setPatientNotReadyForSurgeonMutation] = useMutation(setPatientNotReadyForSurgeon);
  const [dischargeMutation] = useMutation(discharge);
  const [dischargePhysicianLastPatientMutation] = useMutation(dischargePhysicianLastPatient);
  const [dischargeAnesthesiologistLastPatientMutation] = useMutation(dischargeAnesthesiologistLastPatient);
  const [dischargeBothLastPatientMutation] = useMutation(dischargeBothLastPatient);
  const [transferToChairMutation] = useMutation(transferToChair);
  const [transferToPTMutation] = useMutation(transferToPT);
  const [ptCompleteMutation] = useMutation(ptComplete);
  const [setRoomMutation] = useSetRoomMutation();
  const [setReadyToSeeFamilyPreOpMutation] = useMutation(setReadyToSeeFamilyPreOp);
  const [setNotReadyToSeeFamilyPreOpMutation] = useMutation(setNotReadyToSeeFamilyPreOp);
  const [editPatientOobWarningDelayReasonMutation] = useMutation(editPatientOobWarningDelayReason);
  const [editPatientPtWarningDelayReasonMutation] = useMutation(editPatientPtWarningDelayReason);
  const [editPatientPtCompleteWarningDelayReasonMutation] = useMutation(editPatientPtCompleteWarningDelayReason);
  const [editPatientPacuOutWarningDelayReasonMutation] = useMutation(editPatientPacuOutWarningDelayReason);
  const [setPatientEventTimestampMutation] = useMutation(setPatientEventTimestamp);

  return {
    // Bed & Priority
    assignBed: assignBedMutation,
    assignPriority: assignPriorityMutation,

    // Ready Status
    setPatientReadyInPACU: setPatientReadyInPACUMutation,
    setPatientReadyInPOSTOP: setPatientReadyInPOSTOPMutation,
    setPatientReadyInPREP: setPatientReadyInPREPMutation,
    setReadyForAnesthesia: setReadyForAnesthesiaMutation,
    setSpinalTimeout: setSpinalTimeoutMutation,
    setPatientReadyForSurgeon: setPatientReadyForSurgeonMutation,

    // Not Ready Status
    setPatientNotReadyInPREP: setPatientNotReadyInPREPMutation,
    setPatientNotReadyInPACU: setPatientNotReadyInPACUMutation,
    setPatientNotReadyInPOSTOP: setPatientNotReadyInPOSTOPMutation,
    setNotReadyForAnesthesia: setNotReadyForAnesthesiaMutation,
    setUndoSpinalTimeout: setUndoSpinalTimeoutMutation,
    setPatientNotReadyForSurgeon: setPatientNotReadyForSurgeonMutation,

    // Family Ready
    setFamilyReadyForPickupPACU: setFamilyReadyForPickupPACUMutation,
    setFamilyReadyForPickupPOSTOP: setFamilyReadyForPickupPOSTOPMutation,
    setReadyToSeeFamilyPreOp: setReadyToSeeFamilyPreOpMutation,
    setNotReadyToSeeFamilyPreOp: setNotReadyToSeeFamilyPreOpMutation,

    // Hold & Block
    setHoldProcedure: setHoldProcedureMutation,
    setUndoHoldProcedure: setUndoHoldProcedureMutation,
    setBlockNerve: setBlockNerveMutation,
    setUndoBlockNerve: setUndoBlockNerveMutation,

    // Discharge & Transfer
    discharge: dischargeMutation,
    dischargePhysicianLastPatient: dischargePhysicianLastPatientMutation,
    dischargeAnesthesiologistLastPatient: dischargeAnesthesiologistLastPatientMutation,
    dischargeBothLastPatient: dischargeBothLastPatientMutation,
    transferToChair: transferToChairMutation,
    transferToPT: transferToPTMutation,
    ptComplete: ptCompleteMutation,

    // Room
    moveToOr: setRoomMutation, // Alias for setRoom
    setRoom: setRoomMutation,

    // Delay Reasons
    editPatientOobWarningDelayReason: editPatientOobWarningDelayReasonMutation,
    editPatientPtWarningDelayReason: editPatientPtWarningDelayReasonMutation,
    editPatientPtCompleteWarningDelayReason: editPatientPtCompleteWarningDelayReasonMutation,
    editPatientPacuOutWarningDelayReason: editPatientPacuOutWarningDelayReasonMutation,

    // Time Edit
    setPatientEventTimestamp: setPatientEventTimestampMutation,
  };
}

export type PatientMutations = ReturnType<typeof usePatientMutations>;
